import Paper from "@mui/material/Paper";
import {
    Autocomplete,
    Box,
    Checkbox,
    Chip,
    Collapse,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    FormControl,
    FormControlLabel,
    Grid2 as Grid,
    IconButton,
    InputLabel,
    ListItem,
    MenuItem,
    OutlinedInput,
    Select,
    Stack,
    TextField,
    Tooltip,
    Typography
} from "@mui/material";
import Button from "@mui/material/Button";
import List from "@mui/material/List";
import ListItemText from "@mui/material/ListItemText";
import React, {useEffect, useState} from "react";
import productProjectService from "../../services/ProductProjectService";
import projectTaskService from "../../services/ProjectTaskService";
import {
    Add,
    AttachFile,
    Close,
    Delete,
    ExpandLess,
    ExpandMore,
    Launch,
    Remove,
    Settings,
    Star,
    StarHalf,
    StarOutline,
    UnfoldLess
} from "@mui/icons-material";
import projectService from "../../services/ProjectService";
import {useSnackbar} from "../../util/SnackbarProvider";
import {useNavigate} from "react-router-dom";
import {INVOICE_TYPES} from "../../util/Const";
import {BarChart} from "@mui/x-charts/BarChart";
import {huDateFormatter, parseToHUF} from "../../util/StringUtils";
import userService from "../../services/UserService";
import {LineChart} from "@mui/x-charts";

function valueFormatter(value) {
    return `${value?.toLocaleString()} Ft`;
}

const LOCAL_STORAGE_NAME = "project-structure"
const ACCORDION_STATES = "project-structure-accordion-states"

const ProjectStructure = () => {

    const [relevanceFilter, setRelevanceFilter] = useState('ALL');
    const [project, setProject] = useState(() => {
        try {
            return JSON.parse(localStorage.getItem(LOCAL_STORAGE_NAME))
        } catch {
            return null
        }
    });

    const [projectFiles, setProjectFiles] = useState([]);
    const [selectedProjectFiles, setSelectedProjectFiles] = useState([]);
    const [projectDetails, setProjectDetails] = useState([]);

    const [monthlyCosts, setMonthlyCosts] = useState([]);
    const [selectedProductProjects, setSelectedProductProjects] = useState([]);
    const [selectedProjectTasks, setSelectedProjectTasks] = useState([]);
    const [invoices, setInvoices] = useState([]);
    const [draftInvoices, setDraftInvoices] = useState([]);
    const {sendAlertMessage} = useSnackbar()
    const navigate = useNavigate();
    const [resetStatesButtonEnabled, setResetStatesButtonEnabled] = useState(false);

    const [accordionStates, setAccordionStates] = useState(JSON.parse(localStorage.getItem(ACCORDION_STATES)) || {});
    const flipState = (name) => {
        setAccordionStates(prev => {
            const next = {...prev, [name]: !prev[name]}
            localStorage.setItem(ACCORDION_STATES, JSON.stringify(next))
            return next;
        });
    };

    const [openStateSettings, setOpenStateSettings] = useState(false);
    const onSettingsClose = () => {
        setOpenStateSettings(false);
    }

    const collapseAll = () => {
        setAccordionStates({});
    }

    useEffect(() => {
        retrieveProjectStructure()
    }, [project?.id]);

    const retrieveProjectStructure = () => {
        if (project?.id == null) return
        projectService.findStructureById(project.id)
            .then(response => {
                setMonthlyCosts((response.monthlyCosts))
                setProjectDetails(response.details)
                setProjectFiles(response.files)
                setSelectedProjectTasks(response.projectTasks)
                setSelectedProductProjects(response.productProjects)
                setInvoices(response.invoices)
                setDraftInvoices(response.draftInvoices)
            })
            .catch(error => sendAlertMessage(error?.message || "Projekt lekérése sikertelen", 'error'));
    }

    const [projects, setProjects] = useState([]);
    useEffect(() => {
        projectService.getSelectors().then(setProjects).catch(console.error);
    }, []);

    const [users, setUsers] = useState([]);
    useEffect(() => {
        userService.getSelectors().then(setUsers).catch(console.error);
    }, []);

    const [projectStates, setProjectStates] = useState([]);
    useEffect(() => {
        retrieveProjectStates()
    }, []);

    const retrieveProjectStates = () => {
        projectService.getProjectStates()
            .then(response => {
                setProjectStates(response)
                setResetStatesButtonEnabled(false)
            })
            .catch(console.error);
    }

    const removeProjectStateAt = (index) => {
        setResetStatesButtonEnabled(true)
        setProjectStates(prev =>
            prev.filter((_, i) => i !== index)
        );
    };

    const addProjectState = () => {
        setResetStatesButtonEnabled(true)
        setProjectStates(prev => [
            ...prev,
            {id: null, name: ''}
        ]);
    };

    const onProjectStateChange = (event, index) => {
        setResetStatesButtonEnabled(true)
        const value = event.target.value;
        const name = event.target.name;

        setProjectStates(prev =>
            prev.map((ps, i) => i === index ? {...ps, [name]: value} : ps)
        );
    };

    const updateProjectStates = (e) => {
        e.preventDefault()
        projectService.updateProjectStates(projectStates)
            .then(() => {
                retrieveProjectStates()
                setResetStatesButtonEnabled(false)
                sendAlertMessage('Projekt státusz opciók sikeresen módosítva')
            })
            .catch(error => sendAlertMessage(error?.message || 'Projekt státuszok mentése sikertelen', 'error'))
    }

    const [filteredProjects, setFilteredProjects] = useState([]);
    useEffect(() => {
        switch (relevanceFilter) {
            case 'ALL':
                setFilteredProjects(projects);
                break;
            case 'RELEVANTS_ONLY':
                setFilteredProjects(projects.filter(p => p.relevance === 'RELEVANT'));
                break;
            case 'SEMI_RELEVANTS_ONLY':
                setFilteredProjects(projects.filter(p => p.relevance === 'SEMI_RELEVANT'));
                break;
            default:
                setFilteredProjects(projects);
                break;
        }
    }, [projects, relevanceFilter]);

    const [productProjects, setProductProjects] = useState([]);
    useEffect(() => {
        productProjectService.getSelectors().then(setProductProjects).catch(console.error);
    }, []);

    const [projectTasks, setProjectTasks] = useState([]);
    useEffect(() => {
        projectTaskService.getSelectors()
            .then(selectors => setProjectTasks(selectors.map(selector => ({description: selector}))))
            .catch(console.error);
    }, []);

    const onProductProjectChange = (e, index) => {
        const {name, value} = e.target
        setSelectedProductProjects(prev =>
            prev.map((item, i) => i === index ? {...item, [name]: value} : item)
        );
    };

    const onDraftInvoiceChange = (e, index) => {
        const {name, value} = e.target
        setDraftInvoices(prev =>
            prev.map((item, i) => i === index ? {...item, [name]: value} : item)
        );
    };

    const onProjectDetailsChange = (e) => {
        const {name, value} = e.target
        setProjectDetails(prev => ({
            ...prev, [name]: value
        }));
    };

    const onProjectDetailsCheckboxChange = (event) => {
        const {checked, name} = event.target
        setProjectDetails((prev) => ({
            ...prev,
            [name]: checked,
        }));
    };

    const onTaskChange = (projectTaskIndex, taskIndex, name, newValue) => {
        console.log(projectTaskIndex, taskIndex, name, newValue);
        setSelectedProjectTasks(prev =>
            prev.map((project, pIdx) => pIdx === projectTaskIndex ? {
                    ...project,
                    tasks: project?.tasks?.map((task, tIdx) => tIdx === taskIndex ? {...task, [name]: newValue} : task)
                } : project
            )
        );
    };


    const handleFileChange = (event) => {
        const selectedFiles = Array.from(event.target.files);
        setSelectedProjectFiles((prevFiles) => {
            const newFiles = selectedFiles.filter(
                (file) => !prevFiles.some((f) => f.name === file.name && f.size === file.size)
            );
            return [...prevFiles, ...newFiles];
        });
        event.target.value = null;
    };

    const handleRemoveFile = (indexToRemove) => {
        setSelectedProjectFiles((prevFiles) => prevFiles.filter((_, index) => index !== indexToRemove));
    };

    const handleProductProjectsChange = (index, newValue) => {
        setSelectedProductProjects((prev) => {
            const updated = [...prev];
            updated[index] = {
                id: newValue?.id,
                name: newValue?.name,
                amount: updated[index]?.amount
            };
            return updated;
        });
    };

    const handleAddProductProject = () => {
        setSelectedProductProjects((prev) => [...prev, null]);
    };

    const handleRemoveProductProject = (index) => {
        setSelectedProductProjects((prev) => prev.filter((_, i) => i !== index));
    };

    const handleProjectTaskChange = (index, newValue) => {
        setSelectedProjectTasks((prev) => {
            const updated = [...prev];
            updated[index] = newValue
            return updated;
        });
    };

    const handleAddProjectTask = () => {
        setSelectedProjectTasks((prev) => [...prev, null]);
    };

    const handleRemoveProjectTask = (index) => {
        setSelectedProjectTasks((prev) => prev.filter((_, i) => i !== index));
    };

    const handleRemoveTaskOfProjectTask = (index, taskIndex) => {
        setSelectedProjectTasks((prev) =>
            prev.map((projectTask, i) => {
                if (i !== index) return projectTask;
                return {
                    ...projectTask,
                    tasks: projectTask.tasks.filter((_, j) => j !== taskIndex)
                };
            })
        );
    };

    const handleAddTaskToProjectTask = (index, newTask) => {
        setSelectedProjectTasks((prev) =>
            prev.map((projectTask, i) => {
                if (i !== index) return projectTask;
                return {
                    ...projectTask,
                    tasks: [...(projectTask?.tasks || []), newTask]
                };
            })
        );
    };


    const handleAddDraftInvoice = () => {
        setDraftInvoices((prev) => [...prev, null]);
    };

    const handleRemoveDraftInvoice = (index) => {
        setDraftInvoices((prev) => prev.filter((_, i) => i !== index));
    };

    const uploadProjectFiles = () => {
        if (project?.id == null) return
        projectService.uploadProjectFiles(project.id, selectedProjectFiles)
            .then(() => {
                setSelectedProjectFiles([])
                retrieveProjectStructure()
                sendAlertMessage("Projekt mellékletek feltöltése sikeres")
            })
            .catch(error => sendAlertMessage(error?.message || "Projekt mellékletek feltöltése sikertelen", 'error'));
    }

    const updateProductProjects = () => {
        if (project?.id == null) return
        projectService.updateProductProjects(project.id, {productProjects: selectedProductProjects, productProjectsDescription: projectDetails?.productProjectsDescription})
            .then(() => {
                retrieveProjectStructure()
                sendAlertMessage("Termék projektek mentve")
            })
            .catch(error => sendAlertMessage(error?.message || "Termék projektek mentése sikertelen", 'error'));
    }

    const updateProjectDetails = () => {
        if (project?.id == null) return
        projectService.updateProjectDetails(project.id, projectDetails)
            .then(() => {
                retrieveProjectStructure()
                sendAlertMessage("Adatok mentve")
            })
            .catch(error => sendAlertMessage(error?.message || "Adatok mentése sikertelen", 'error'));
    }

    const updateProjectTasks = () => {
        if (project?.id == null) return
        projectService.updateProjectTasks(project.id, {projectTasks: selectedProjectTasks, projectTasksDescription: projectDetails?.projectTasksDescription})
            .then(() => {
                retrieveProjectStructure()
                sendAlertMessage("Fejlécek mentve")
            })
            .catch(error => sendAlertMessage(error?.message || "Fejlécek mentése sikertelen", 'error'));
    }

    const updateDraftInvoices = () => {
        if (project?.id == null) return
        projectService.updateDraftInvoices(project.id,
            {
                draftInvoices: draftInvoices,
                draftInvoicesDescription: projectDetails?.draftInvoicesDescription,
                otherPlannedExpenses: projectDetails?.otherPlannedExpenses
            }
        )
            .then(() => {
                retrieveProjectStructure()
                sendAlertMessage("Számla tervek mentve")
            })
            .catch(error => sendAlertMessage(error?.message || "Számla tervek mentése sikertelen", 'error'));
    }

    const deleteProjectFile = (fileId) => {
        if (project?.id == null) return
        projectService.deleteProjectFile(project.id, fileId)
            .then(() => {
                retrieveProjectStructure()
                sendAlertMessage('Fájl sikeresen törölve!')
            })
            .catch(error => sendAlertMessage(error?.message || "Fájl törlése sikertelen", 'error'))
    };

    const toggleRelevance = (id) => {
        projectService.toggleRelevance(id)
            .then(response => {
                setProjectDetails(prev => ({...prev, relevance: response}))
            })
            .catch(error => sendAlertMessage(error?.message || "Művelet sikertelen", 'error'))
    }

    const toggleRelevanceFilter = () => {
        switch (relevanceFilter) {
            case 'ALL':
                setRelevanceFilter('RELEVANTS_ONLY');
                break;
            case 'RELEVANTS_ONLY':
                setRelevanceFilter('SEMI_RELEVANTS_ONLY');
                break;
            case 'SEMI_RELEVANTS_ONLY':
                setRelevanceFilter('ALL');
                break;
            default:
                setRelevanceFilter('ALL');
                break;
        }
    }

    const listItemTextProps = {
        primary: {
            noWrap: true,
            sx: {fontSize: "0.9rem"},
        },
        secondary: {
            noWrap: true,
            sx: {fontSize: "0.75rem", color: "text.secondary"},
        }
    }

    const onNextProjectButtonClick = (direction) => {
        if (!project) return; // nothing selected yet

        const index = filteredProjects.findIndex(p => p.id === project.id);
        if (index + direction >= 0 && index + direction < filteredProjects.length) {
            const prevProject = filteredProjects[index + direction];
            setProject(prevProject);
            localStorage.setItem(LOCAL_STORAGE_NAME, JSON.stringify(prevProject));
        }
    };

    const onUsersChange = (projectTaskIndex, taskIndex, event) => {
        const {value} = event.target;
        const newSelection = Array.isArray(value) ? value : value.split(',');

        // toggle logic (add if not exists, remove if exists)
        const uniqueSelection = newSelection.reduce((acc, user) => {
            const exists = acc.find(item => item.id === user.id);
            return exists
                ? acc.filter(item => item.id !== user.id)
                : [...acc, user];
        }, []);

        setSelectedProjectTasks(prev =>
            prev.map((project, pIdx) =>
                pIdx === projectTaskIndex
                    ? {
                        ...project,
                        tasks: project.tasks?.map((task, tIdx) =>
                            tIdx === taskIndex
                                ? {...task, users: uniqueSelection}
                                : task
                        )
                    }
                    : project
            )
        );
    };


    const MenuProps = {
        PaperProps: {
            style: {
                maxHeight: 48 * 4.5 + 8,
                width: 250
            },
        },
    };

    function getStyles(name, users) {
        if (users == null || users.map(user => user.name).indexOf(name) === -1) return
        return {
            backgroundColor: "#2e7d32",
            color: "white"
        };
    }

    const saveAndNavigateToProductProjects = () => {
        projectService.updateProductProjects(project.id, {productProjects: selectedProductProjects, productProjectsDescription: projectDetails?.productProjectsDescription})
            .then(() => {
                navigate("/dashboard/product-projects/create");
            })
            .catch(error => sendAlertMessage(error?.message || "Termék projektek mentése sikertelen", 'error'));
    }

    const timeGraphData = Object.entries(projectDetails?.hoursProgress ?? {})
        .map(([date, value]) => ({
            x: new Date(date),
            y: value,
        }))
        .sort((a, b) => a.x.getTime() - b.x.getTime());

    const financialGraphData = Object.entries(projectDetails?.financialProgress ?? {})
        .map(([date, value]) => ({
            x: new Date(date),
            y: value,
        }))
        .sort((a, b) => a.x.getTime() - b.x.getTime());

    return (
        <Paper sx={{padding: "10px", marginBottom: "30px"}}>
            <Box component="fieldset" sx={{border: "1px solid #e5e7eb", borderRadius: "12px", padding: "1rem", marginBottom: "20px"}}>
                <legend style={{fontWeight: "200", padding: "0 .5rem", color: "#575757"}}>Projekt</legend>

                <Grid container spacing={2} columns={20}>
                    <Grid size={{xs: 5}}>
                        <Autocomplete
                            fullWidth
                            options={filteredProjects}
                            getOptionLabel={(option) => "[" + option.identifier + "] " + option.name}
                            value={project || null}
                            onChange={(event, newValue) => {
                                setProject(newValue)
                                localStorage.setItem(LOCAL_STORAGE_NAME, JSON.stringify(newValue))
                            }}
                            renderInput={(params) => (
                                <TextField label={"Projekt neve"} {...params}/>
                            )}
                            sx={{flexGrow: 1}}
                        />
                    </Grid>
                    <Grid size={{xs: 5}}>

                        <Stack direction="row" alignItems="center" spacing={1}>
                            <Stack spacing={0.5} sx={{width: "24px"}}>
                                <Tooltip title={"Előző"} placement={"right"}>
                                    <IconButton size="small" sx={{margin: "0px", padding: "0px"}} onClick={() => onNextProjectButtonClick(-1)}>
                                        <ExpandLess/>
                                    </IconButton>
                                </Tooltip>

                                <Tooltip title={"Következő"} placement={"right"}>
                                    <IconButton size="small" sx={{margin: "0px", padding: "0px"}} onClick={() => onNextProjectButtonClick(+1)}>
                                        <ExpandMore/>
                                    </IconButton>
                                </Tooltip>
                            </Stack>

                            {relevanceFilter === "RELEVANTS_ONLY" && (
                                <Tooltip title="Csak aktuálisak">
                                    <Button variant="text" sx={{marginTop: "10px", padding: "0px"}} onClick={toggleRelevanceFilter}>
                                        <Star sx={{color: "gray", fontSize: "40px"}}/>
                                    </Button>
                                </Tooltip>
                            )}

                            {relevanceFilter === "SEMI_RELEVANTS_ONLY" && (
                                <Tooltip title="Csak fél-aktuálisak">
                                    <Button variant="text" sx={{marginTop: "10px", padding: "0px"}} onClick={toggleRelevanceFilter}>
                                        <StarHalf sx={{color: "gray", fontSize: "40px"}}/>
                                    </Button>
                                </Tooltip>
                            )}

                            {(relevanceFilter == null || relevanceFilter === "ALL") && (
                                <Tooltip title="Összes">
                                    <Button variant="text" sx={{marginTop: "10px", padding: "0px"}} onClick={toggleRelevanceFilter}>
                                        <StarOutline sx={{color: "gray", fontSize: "40px"}} size="large"/>
                                    </Button>
                                </Tooltip>
                            )}
                        </Stack>
                    </Grid>
                    <Grid size={{xs: 10}}></Grid>
                    <Grid size={{xs: 5}}>
                        {project?.id && <Button variant="contained" sx={{marginTop: "10px"}} onClick={() => navigate("/dashboard/projects/ " + project.id)}>Szerkesztés</Button>}
                        <Button color="success" variant="contained" sx={{marginTop: "10px", marginLeft: "10px"}} onClick={() => navigate("/dashboard/projects/create")}>Új projekt</Button>
                    </Grid>
                </Grid>

                {project?.id &&
                    <>
                        <Tooltip title={"Összes összecsukása"}>
                            <Button variant="text" component="span"
                                    onClick={() => collapseAll()}
                                    sx={{
                                        marginTop: "10px",
                                        marginRight: "10px",
                                        padding: "0px",
                                        minWidth: "0px",
                                        backgroundColor: "#b4b4b6",
                                        "&:hover": {backgroundColor: "#9c9ca0"},
                                    }}>
                                <UnfoldLess fontSize="large" sx={{color: "white", margin: "0px", padding: "5px"}}/>
                            </Button>
                        </Tooltip>
                    </>}
            </Box>

            {project?.id &&
                <>
                    <Box component="fieldset" sx={{border: (accordionStates?.openDetails ? "1px solid #e5e7eb" : "none"), borderRadius: "12px", padding: (accordionStates?.openDetails ? "1rem" : "0px calc(1rem + 1px)"), marginBottom: "20px"}}>
                        <legend style={{fontWeight: "200", padding: "0 .5rem", color: "#575757", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "space-between"}}
                                onClick={() => flipState("openDetails")}>Részletek {accordionStates?.openDetails ? <ExpandLess/> : <ExpandMore/>}
                        </legend>

                        <Collapse in={accordionStates?.openDetails}>
                            <Grid container spacing={2} columns={20}>
                                <Grid size={{xs: 5}}>
                                    <FormControl fullWidth>
                                        <InputLabel id="currentStateLabel">Státusz</InputLabel>
                                        <Select
                                            variant="outlined"
                                            fullWidth
                                            name="currentState"
                                            label="Státusz"
                                            labelId="currentStateLabel"
                                            value={projectDetails?.currentState || ""}
                                            renderValue={value => value?.name}
                                            onChange={onProjectDetailsChange}
                                            sx={{textAlign: "left"}}
                                        >
                                            {projectStates.map((state, index) => (
                                                <MenuItem key={index} value={state}>{state.name}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>

                                <Grid size={{xs: 2}}>
                                    <Tooltip title="Státusz beállítások">
                                        <Button variant="text" sx={{marginTop: "10px", padding: "0px"}} onClick={() => setOpenStateSettings(true)}>
                                            <Settings sx={{color: "gray", fontSize: "40px"}}/>
                                        </Button>
                                    </Tooltip>
                                    {project?.id && (
                                        <span style={{marginRight: "10px"}}>
                                            {projectDetails?.relevance === "RELEVANT" && (
                                                <Tooltip title="Aktuális">
                                                    <Button variant="text" sx={{marginTop: "10px", padding: "0px"}} onClick={() => toggleRelevance(project.id)}>
                                                        <Star sx={{color: "gold", fontSize: "40px"}}/>
                                                    </Button>
                                                </Tooltip>
                                            )}

                                            {projectDetails?.relevance === "SEMI_RELEVANT" && (
                                                <Tooltip title="Fél-aktuális">
                                                    <Button variant="text" sx={{marginTop: "10px", padding: "0px"}} onClick={() => toggleRelevance(project.id)}>
                                                        <StarHalf sx={{color: "gold", fontSize: "40px"}}/>
                                                    </Button>
                                                </Tooltip>
                                            )}

                                            {(projectDetails?.relevance == null || projectDetails?.relevance === "IRRELEVANT") && (
                                                <Tooltip title="Nem aktuális">
                                                    <Button variant="text" sx={{marginTop: "10px", padding: "0px"}} onClick={() => toggleRelevance(project.id)}>
                                                        <StarOutline sx={{color: "gold", fontSize: "40px"}} size="large"/>
                                                    </Button>
                                                </Tooltip>
                                            )}
                                        </span>
                                    )}
                                </Grid>

                                <Grid size={{xs: 5}}>
                                    <FormControlLabel control={<Checkbox
                                        variant="outlined"
                                        margin="normal"
                                        size="large"
                                        name="anythingToDo"
                                        onChange={onProjectDetailsCheckboxChange}
                                        checked={projectDetails?.anythingToDo}
                                    />} label="Van tennivaló"/>
                                </Grid>

                                <Grid size={{xs: 20}}>
                                    <TextField variant={"outlined"} multiline fullWidth label="Projekt leírása" name={"projectDescription"}
                                               value={projectDetails?.projectDescription || ""} minRows={3} maxRows={10} onChange={onProjectDetailsChange}/>
                                </Grid>
                            </Grid>

                            <div>
                                <Button variant="contained" sx={{marginTop: "10px"}} onClick={updateProjectDetails}>Mentés</Button>
                            </div>
                        </Collapse>
                    </Box>

                    <Box component="fieldset"
                         sx={{border: (accordionStates?.openProjectFiles ? "1px solid #e5e7eb" : "none"), borderRadius: "12px", padding: (accordionStates?.openProjectFiles ? "1rem" : "0px calc(1rem + 1px)"), marginBottom: "20px"}}>
                        <legend style={{fontWeight: "200", padding: "0 .5rem", color: "#575757", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "space-between"}}
                                onClick={() => flipState("openProjectFiles")}>Projekt mellékletek {accordionStates?.openProjectFiles ? <ExpandLess/> : <ExpandMore/>}
                        </legend>

                        <Collapse in={accordionStates?.openProjectFiles}>
                            <input accept="*" id="file-upload-input" multiple type="file" style={{display: "none"}} onChange={handleFileChange}/>

                            <label htmlFor="file-upload-input">
                                <Button variant="text" component="span"
                                        sx={{
                                            margin: "10px 0px",
                                            padding: "0px",
                                            minWidth: "0px",
                                            backgroundColor: "#b4b4b6",
                                            "&:hover": {backgroundColor: "#9c9ca0"},
                                        }}>
                                    <AttachFile fontSize="large" sx={{color: "white", margin: 0, padding: "5px",}}/>
                                </Button>
                            </label>

                            {projectFiles.length > 0 && (
                                <List dense disablePadding sx={{mt: 1, border: "1px solid #e0e0e0", borderRadius: 1, overflow: "hidden"}}>
                                    {projectFiles.map((file, index) => (
                                        <ListItem key={index} disablePadding
                                                  sx={{
                                                      padding: "0px 10px",
                                                      display: "flex",
                                                      "& p": {lineHeight: "1.0"},
                                                      alignItems: "center",
                                                      "&:not(:last-child)": {borderBottom: "1px solid #f0f0f0"},
                                                  }}
                                                  secondaryAction={
                                                      <IconButton
                                                          edge="end"
                                                          size="small"
                                                          color="error"
                                                          onClick={() => deleteProjectFile(file.id)}
                                                          sx={{backgroundColor: "#b4b4b6", "&:hover": {backgroundColor: "#9c9ca0"}}}
                                                      >
                                                          <Delete fontSize="small" sx={{color: "white"}}/>
                                                      </IconButton>
                                                  }
                                        >
                                            <ListItemText
                                                slotProps={listItemTextProps}
                                                primary={file.name}
                                                secondary={file.createdAt}
                                            />
                                        </ListItem>
                                    ))}
                                </List>
                            )}

                            {selectedProjectFiles.length > 0 && (
                                <List dense disablePadding sx={{mt: 1, border: "1px solid #e0e0e0", borderRadius: 1, overflow: "hidden", marginBottom: "10px"}}>
                                    {selectedProjectFiles.map((file, index) => (
                                        <ListItem key={index} disablePadding
                                                  sx={{
                                                      padding: "0px 10px",
                                                      display: "flex",
                                                      "& p": {lineHeight: "1.0"},
                                                      alignItems: "center",
                                                      backgroundColor: "#fff9b8",
                                                      "&:not(:last-child)": {borderBottom: "1px solid #f0f0f0"},
                                                  }}
                                                  secondaryAction={
                                                      <IconButton
                                                          edge="end"
                                                          size="small"
                                                          color="error"
                                                          onClick={() => handleRemoveFile(index)}
                                                          sx={{backgroundColor: "#b4b4b6", "&:hover": {backgroundColor: "#9c9ca0"}}}
                                                      >
                                                          <Close fontSize="small" sx={{color: "white"}}/>
                                                      </IconButton>
                                                  }
                                        >
                                            <ListItemText
                                                slotProps={listItemTextProps}
                                                primary={file.name}
                                                secondary={`${(file.size / 1024).toFixed(1)} KB`}
                                            />
                                        </ListItem>
                                    ))}
                                </List>
                            )}
                            <Button variant="contained" sx={{marginLeft: "10px"}} onClick={() => uploadProjectFiles()}>Mentés</Button>
                        </Collapse>
                    </Box>

                    <Box component="fieldset" sx={{border: (accordionStates?.openStats ? "1px solid #e5e7eb" : "none"), borderRadius: "12px", padding: (accordionStates?.openStats ? "1rem" : "0px calc(1rem + 1px)"), marginBottom: "20px"}}>
                        <legend style={{fontWeight: "200", padding: "0 .5rem", color: "#575757", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "space-between"}}
                                onClick={() => flipState("openStats")}>Statisztika {accordionStates?.openStats ? <ExpandLess/> : <ExpandMore/>}
                        </legend>
                        <Collapse in={accordionStates?.openStats}>
                            <LineChart
                                height={400}
                                series={[
                                    {
                                        data: timeGraphData.map(d => d.y),
                                        label: 'Teljesített Órák',
                                        showMark: false,
                                    },
                                    {
                                        data: timeGraphData.map(() => projectDetails?.totalEstimatedHours), // constant line
                                        label: 'Becsült Órák',
                                        showMark: false,
                                        strokeDasharray: '4 2',       // optional dashed style
                                        color: 'red',
                                    }
                                ]}
                                xAxis={[
                                    {
                                        data: timeGraphData.map(d => d.x),
                                        scaleType: 'time',
                                        valueFormatter: (date: Date) => huDateFormatter.format(date),
                                        label: 'Idő',
                                    },
                                ]}
                                yAxis={[
                                    {
                                        position: 'left',
                                        min: 0,
                                    },
                                ]}
                                margin={{left: 100}}
                            />

                            <LineChart
                                height={400}
                                series={[
                                    {
                                        data: financialGraphData?.map(d => d.y),
                                        label: 'Kifizetett Számlák',
                                        showMark: false,
                                    },
                                    {
                                        data: financialGraphData?.map(() =>
                                            draftInvoices?.reduce((total, i) => total + (i?.amount || 0), 0)
                                        ),
                                        label: 'Becsült Összeg',
                                        showMark: false,
                                        strokeDasharray: '4 2',       // optional dashed style
                                        color: 'red',
                                    }
                                ]}
                                xAxis={[
                                    {
                                        data: financialGraphData?.map(d => d.x),
                                        scaleType: 'time',
                                        valueFormatter: (date: Date) => huDateFormatter.format(date),
                                        label: 'Idő',
                                    },
                                ]}
                                yAxis={[
                                    {
                                        position: 'left',
                                        min: 0,
                                    },
                                ]}
                                margin={{left: 100}}
                            />

                            <BarChart
                                dataset={Object.entries(monthlyCosts).map(([key, value]) => {
                                    return {
                                        month: key.slice(0, 7),
                                        totalPaidExpense: value[0],
                                        totalUnpaidExpense: value[1],
                                        totalPaidIncome: value[2],
                                        totalUnpaidIncome: value[3]

                                    }
                                })}
                                series={[
                                    {
                                        dataKey: "totalPaidExpense",
                                        label: "Teljesített Kiadás",
                                        color: "#cc0000",
                                        stack: 'expense',
                                        valueFormatter: valueFormatter
                                    },
                                    {
                                        dataKey: "totalUnpaidExpense",
                                        label: "Teljesítetlen Kiadás",
                                        color: "#efb2b2",
                                        stack: 'expense',
                                        valueFormatter: valueFormatter
                                    },
                                    {
                                        dataKey: "totalPaidIncome",
                                        label: "Teljesített Bevétel",
                                        color: "green",
                                        stack: 'income',
                                        valueFormatter
                                    },
                                    {
                                        dataKey: "totalUnpaidIncome",
                                        label: "Teljesítetlen Bevétel",
                                        color: "#b2d8b2",
                                        stack: 'income',
                                        valueFormatter
                                    }
                                ]}
                                xAxis={[{scaleType: 'band', dataKey: 'month'}]}
                                yAxis={[{position: "left", valueFormatter: (value) => value.toLocaleString() + ' Ft'}]}
                                margin={{top: 50, bottom: 30, right: 40, left: 100}}
                                height={400}
                            />
                        </Collapse>
                    </Box>

                    <Box component="fieldset"
                         sx={{border: (accordionStates?.openProductProjects ? "1px solid #e5e7eb" : "none"), borderRadius: "12px", padding: (accordionStates?.openProductProjects ? "1rem" : "0px calc(1rem + 1px)"), marginBottom: "20px"}}>
                        <legend style={{fontWeight: "200", padding: "0 .5rem", color: "#575757", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "space-between"}}
                                onClick={() => flipState("openProductProjects")}>Termék projektek {accordionStates?.openProductProjects ? <ExpandLess/> : <ExpandMore/>}
                        </legend>

                        <Collapse in={accordionStates?.openProductProjects}>
                            <Grid container spacing={1} columns={20}>
                                {selectedProductProjects.map((selected, index) => (
                                    <React.Fragment key={index}>
                                        <Grid size={{xs: 10}} sx={{display: "flex", alignItems: "center", gap: "8px"}}>
                                            <Autocomplete
                                                fullWidth
                                                options={productProjects}
                                                getOptionLabel={(option) => option?.name || ""}
                                                value={selected || null}
                                                onChange={(event, newValue) => handleProductProjectsChange(index, newValue)}
                                                renderInput={(params) => (
                                                    <TextField label={`${index + 1}. Termék projekt`} {...params} />
                                                )}
                                                sx={{flexGrow: 1}}
                                            />
                                            <Tooltip title="Tovább a Termék Projektre" placement="right">
                                                <Button variant="text" component="span" onClick={() => navigate("/dashboard/product-projects/" + selected?.id)}
                                                        sx={{margin: "10px 0px", padding: "0px", minWidth: "0px", backgroundColor: "#b4b4b6", "&:hover": {backgroundColor: "#9c9ca0"},}}>
                                                    <Launch fontSize="large" sx={{color: "white", margin: 0, padding: "5px",}}/>
                                                </Button>
                                            </Tooltip>
                                        </Grid>
                                        <Grid size={{xs: 10}} sx={{display: "flex", alignItems: "center"}}>
                                            <TextField
                                                fullWidth
                                                name="amount"
                                                value={selected?.amount || ""}
                                                onChange={e => onProductProjectChange(e, index)}
                                                label="Mennyiség"
                                                variant="outlined"
                                                type="number"
                                            />
                                            <IconButton color="error" onClick={() => handleRemoveProductProject(index)}
                                                        sx={{marginLeft: "10px", backgroundColor: "#b4b4b6", "&:hover": {backgroundColor: "#9c9ca0"}}}>
                                                <Remove sx={{color: "white"}}/>
                                            </IconButton>
                                        </Grid>
                                        <Grid size={{xs: 20}} sx={{marginBottom: "20px"}}>
                                            <span><b>Teljes Beszerzési Ár: </b>{parseToHUF(selected?.totalPurchaseCost)} | </span>
                                            <span><b>Teljes Eladási Ár:  </b>{parseToHUF(selected?.totalSellingPrice)} | </span>
                                            <span><b>Teljes Belső Munkadíj:  </b>{parseToHUF(selected?.totalInternalLaborCost)} | </span>
                                            <span><b>Teljes Külső Munkadíj:  </b>{parseToHUF(selected?.totalExternalLaborPrice)} | </span>
                                            <span><b>Összesen:  </b>{parseToHUF(selected?.total)}</span>
                                        </Grid>
                                    </React.Fragment>
                                ))}
                            </Grid>

                            <Button
                                variant="text"
                                sx={{
                                    margin: "10px 0px",
                                    padding: "0px",
                                    minWidth: "0px",
                                    backgroundColor: "#b4b4b6",
                                    "&:hover": {backgroundColor: "#9c9ca0"}
                                }}
                                onClick={handleAddProductProject}
                            >
                                <Add fontSize="large" sx={{color: "white"}}/>
                            </Button>

                            <TextField variant={"outlined"} multiline fullWidth label="Termék projekt megjegyzés" name={"productProjectsDescription"}
                                       value={projectDetails?.productProjectsDescription || ""} minRows={3} maxRows={10} onChange={onProjectDetailsChange}/>

                            <div>
                                <Button variant="contained" sx={{marginTop: "10px"}} onClick={updateProductProjects}>Mentés</Button>
                                <Button variant="contained" color={"success"} sx={{marginTop: "10px", marginLeft: "10px"}} onClick={saveAndNavigateToProductProjects}>Új Termék Projekt</Button>
                            </div>
                        </Collapse>
                    </Box>

                    <Box component="fieldset"
                         sx={{border: (accordionStates?.openProjectTasks ? "1px solid #e5e7eb" : "none"), borderRadius: "12px", padding: (accordionStates?.openProjectTasks ? "1rem" : "0px calc(1rem + 1px)"), marginBottom: "20px"}}>
                        <legend style={{fontWeight: "200", padding: "0 .5rem", color: "#575757", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "space-between"}}
                                onClick={() => flipState("openProjectTasks")}>Fejlécek {accordionStates?.openProjectTasks ? <ExpandLess/> : <ExpandMore/>}
                        </legend>

                        <Collapse in={accordionStates?.openProjectTasks}>
                            <Grid container spacing={1} sx={{marginBottom: "8px"}} columns={20}>
                                {selectedProjectTasks.map((selected, index) => (
                                    <>
                                        <Grid key={index} sx={{display: "flex", alignItems: "center"}} size={{xs: 20}}>
                                            <Autocomplete
                                                fullWidth
                                                freeSolo={true}
                                                options={projectTasks}
                                                getOptionLabel={(option) => option?.description || ""}
                                                value={selected || ""}
                                                onChange={(event, newValue) => handleProjectTaskChange(index, newValue)}
                                                onInputChange={(event, newInputValue) => {
                                                    handleProjectTaskChange(index, {
                                                        ...selectedProjectTasks[index],
                                                        description: newInputValue,
                                                    });
                                                }}
                                                renderInput={(params) => (
                                                    <TextField label={`${index + 1}. Fejléc`} {...params} />
                                                )}
                                                sx={{flexGrow: 1}}
                                            />
                                            <IconButton
                                                color="error"
                                                onClick={() => handleRemoveProjectTask(index)}
                                                sx={{marginLeft: "10px", backgroundColor: "#b4b4b6", "&:hover": {backgroundColor: "#9c9ca0"}}}
                                            >
                                                <Remove sx={{color: "white"}}/>
                                            </IconButton>


                                        </Grid>

                                        {(selected?.tasks?.length == null || selected.tasks.length === 0) &&
                                            <Grid size={{xs: 1}}>
                                                <Tooltip title="Feladat hozzáadása" placement="right">
                                                    <IconButton
                                                        color="error"
                                                        onClick={() => handleAddTaskToProjectTask(index, 0)}
                                                        sx={{marginLeft: "10px", marginTop: "0px", backgroundColor: "#b4b4b6", "&:hover": {backgroundColor: "#9c9ca0"}}}
                                                    >
                                                        <Add sx={{color: "white"}} fontSize="small"/>
                                                    </IconButton>
                                                </Tooltip>
                                            </Grid>}

                                        {selected?.tasks?.map((task, taskIndex) => (
                                            <React.Fragment key={index + "-" + taskIndex}>
                                                <Grid size={{xs: 1}} sx={{textAlign: "center"}}>
                                                    <Tooltip title="Tovább a feladatra" placement="left">
                                                        <Button variant="text" component="span" onClick={() => navigate("/dashboard/tasks/ " + task.id)}
                                                                sx={{margin: "16px 0px 0px 0px", padding: "0px", minWidth: "0px", backgroundColor: "#b4b4b6", "&:hover": {backgroundColor: "#9c9ca0"},}}>
                                                            <Launch fontSize="large" sx={{color: "white", margin: 0, padding: "5px",}}/>
                                                        </Button>
                                                    </Tooltip>
                                                </Grid>

                                                <Grid size={{xs: 5}}>
                                                    <TextField variant={"outlined"} fullWidth label="Feladat leírása" name={"description"}
                                                               value={task?.description || ""} onChange={(e) => onTaskChange(index, taskIndex, e.target.name, e.target.value)} sx={{marginTop: "8px"}}/>
                                                </Grid>

                                                <Grid size={{xs: 2}}>
                                                    <FormControl sx={{
                                                        m: 1,
                                                        minWidth: 120,
                                                        marginLeft: "0px"
                                                    }} fullWidth>
                                                        <InputLabel id="priority-label">Prioritás</InputLabel>
                                                        <Select
                                                            variant="outlined"
                                                            fullWidth
                                                            labelId="priority-label"
                                                            name="priority"
                                                            label="Prioritás"
                                                            value={task.priority || "NORMAL"}
                                                            defaultValue={"NORMAL"}
                                                            onChange={(e) => onTaskChange(index, taskIndex, e.target.name, e.target.value)}
                                                            sx={{textAlign: "left"}}
                                                        >
                                                            <MenuItem value={"EXTRA_IMPORTANT"}>Extra fontos</MenuItem>
                                                            <MenuItem value={"IMPORTANT"}>Fontos</MenuItem>
                                                            <MenuItem value={"NORMAL"}>Normál</MenuItem>
                                                            <MenuItem value={"NOT_IMPORTANT"}>Nem fontos</MenuItem>
                                                        </Select>
                                                    </FormControl>
                                                </Grid>

                                                <Grid size={{xs: 2}}>
                                                    <FormControl sx={{
                                                        m: 1,
                                                        minWidth: 120,
                                                        marginLeft: "0px"
                                                    }} fullWidth>
                                                        <InputLabel id="status-label">Státusz</InputLabel>
                                                        <Select
                                                            variant="outlined"
                                                            fullWidth
                                                            labelId="status-label"
                                                            name="status"
                                                            label="Státusz"
                                                            value={task.status || "NOT_STARTED"}
                                                            defaultValue={"NOT_STARTED"}
                                                            onChange={(e) => onTaskChange(index, taskIndex, e.target.name, e.target.value)}
                                                            sx={{textAlign: "left"}}
                                                        >
                                                            <MenuItem value={"NOT_STARTED"}>Nincs elkezdve</MenuItem>
                                                            <MenuItem value={"IN_PROGRESS"}>Folyamatban</MenuItem>
                                                            <MenuItem value={"BLOCKED"}>Elakadva</MenuItem>
                                                            <MenuItem value={"OVERDUE"}>Csúszásban</MenuItem>
                                                            <MenuItem value={"DONE"}>Befejezve</MenuItem>
                                                        </Select>
                                                    </FormControl>
                                                </Grid>

                                                <Grid size={{xs: 2}}>
                                                    <TextField
                                                        variant="outlined"
                                                        margin="normal"
                                                        fullWidth
                                                        type="number"
                                                        name="estimatedHours"
                                                        label="Becsült órák"
                                                        autoComplete="off"
                                                        onChange={(e) => onTaskChange(index, taskIndex, e.target.name, e.target.value)}
                                                        sx={{marginTop: "8px"}}
                                                        value={task.estimatedHours || ""}
                                                    />
                                                </Grid>

                                                <Grid size={{xs: 6}}>
                                                    <FormControl sx={{m: 1, minWidth: 120, marginTop: "8px", marginLeft: "0px"}} fullWidth>
                                                        <InputLabel id="users-label">Dolgozók</InputLabel>
                                                        <Select
                                                            labelId="users-label"
                                                            multiple
                                                            value={task.users || []}
                                                            onChange={(e) => onUsersChange(index, taskIndex, e)}
                                                            label="Dolgozók"
                                                            variant="outlined"
                                                            input={<OutlinedInput id="select-multiple-chip"
                                                                                  label="Dolgozók"/>}
                                                            renderValue={(selected) => (
                                                                <Box sx={{
                                                                    display: 'flex',
                                                                    flexWrap: 'wrap',
                                                                    gap: 1.0,
                                                                    margin: "-5px"
                                                                }}>
                                                                    {selected.map((value, i) => (
                                                                        <Chip key={i} label={value.name}/>
                                                                    ))}
                                                                </Box>
                                                            )}
                                                            MenuProps={MenuProps}
                                                        >
                                                            {users.map((user, i) => (
                                                                <MenuItem
                                                                    key={index + "-" + taskIndex + "-" + i}
                                                                    value={user}
                                                                    style={getStyles(user.name, task.users)}
                                                                >
                                                                    {user.name}
                                                                </MenuItem>
                                                            ))}
                                                        </Select>
                                                    </FormControl>
                                                </Grid>

                                                <Grid size={{xs: 2}}>
                                                    <IconButton
                                                        color="error"
                                                        onClick={() => handleRemoveTaskOfProjectTask(index, taskIndex)}
                                                        sx={{marginLeft: "10px", marginTop: "20px", backgroundColor: "#b4b4b6", "&:hover": {backgroundColor: "#9c9ca0"}}}
                                                    >
                                                        <Remove sx={{color: "white"}} fontSize="small"/>
                                                    </IconButton>

                                                    {taskIndex === selected?.tasks?.length - 1 &&
                                                        <Tooltip title="Feladat hozzáadása" placement="right">
                                                            <IconButton
                                                                color="error"
                                                                onClick={() => handleAddTaskToProjectTask(index, taskIndex)}
                                                                sx={{marginLeft: "10px", marginTop: "20px", backgroundColor: "#b4b4b6", "&:hover": {backgroundColor: "#9c9ca0"}}}
                                                            >
                                                                <Add sx={{color: "white"}} fontSize="small"/>
                                                            </IconButton>
                                                        </Tooltip>}
                                                </Grid>
                                            </React.Fragment>
                                        ))}
                                    </>
                                ))}
                            </Grid>

                            <Tooltip title="Fejléc hozzáadása" placement="right">
                                <Button variant="text" sx={{
                                    margin: "10px 0px",
                                    padding: "0px",
                                    minWidth: "0px",
                                    backgroundColor: "#b4b4b6",
                                    "&:hover": {backgroundColor: "#9c9ca0"}
                                }} onClick={handleAddProjectTask}>
                                    <Add fontSize="large" color="action" sx={{
                                        color: "white",
                                        marginTop: "0px",
                                        marginLeft: "0px"
                                    }}/>
                                </Button>
                            </Tooltip>

                            <TextField variant={"outlined"} multiline fullWidth label="Fejléc megjegyzés" name={"projectTasksDescription"}
                                       value={projectDetails?.projectTasksDescription || ""} minRows={3} maxRows={10} onChange={onProjectDetailsChange}/>

                            <div>
                                <Button variant="contained" sx={{marginTop: "10px"}} onClick={() => updateProjectTasks()}>Mentés</Button>
                            </div>
                        </Collapse>
                    </Box>

                    <Box component="fieldset"
                         sx={{border: (accordionStates?.openDraftInvoices ? "1px solid #e5e7eb" : "none"), borderRadius: "12px", padding: (accordionStates?.openDraftInvoices ? "1rem" : "0px calc(1rem + 1px)"), marginBottom: "20px"}}>
                        <legend style={{fontWeight: "200", padding: "0 .5rem", color: "#575757", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "space-between"}}
                                onClick={() => flipState("openDraftInvoices")}>Számla Tervek {accordionStates?.openDraftInvoices ? <ExpandLess/> : <ExpandMore/>}
                        </legend>
                        <Collapse in={accordionStates?.openDraftInvoices}>
                            <Grid container spacing={1} columns={20}>
                                {draftInvoices.map((invoice, index) => (
                                    <React.Fragment key={index}>
                                        <Grid size={{xs: 4}} sx={{display: "flex"}}>
                                            <FormControl fullWidth>
                                                <InputLabel id="invoice-type-label">Számla típusa</InputLabel>
                                                <Select
                                                    variant="outlined"
                                                    labelId="invoice-type-label"
                                                    name="invoiceType"
                                                    value={invoice?.invoiceType || ""}
                                                    onChange={(e) => onDraftInvoiceChange(e, index)}
                                                    label="Számla típusa"
                                                    sx={{textAlign: "left"}}
                                                >
                                                    <MenuItem value={"INVOICE"}>Számla</MenuItem>
                                                    <MenuItem value={"ADVANCE_INVOICE"}>Előlegszámla</MenuItem>
                                                    <MenuItem value={"FEE_REQUEST"}>Díjbekérő</MenuItem>
                                                    <MenuItem value={"RECEIPT"}>Nyugta</MenuItem>
                                                </Select>
                                            </FormControl>
                                        </Grid>
                                        <Grid size={{xs: 4}}>
                                            <TextField name="amount" value={invoice?.amount || ""} onChange={(e) => onDraftInvoiceChange(e, index)} fullWidth label="Összeg (HUF)"/>
                                        </Grid>
                                        <Grid size={{xs: 4}}>
                                            <TextField name="comment" value={invoice?.comment || ""} onChange={(e) => onDraftInvoiceChange(e, index)} fullWidth label="Megjegyzés"/>
                                        </Grid>
                                        <Grid size={{xs: 8}}>
                                            <IconButton color="error" onClick={() => handleRemoveDraftInvoice(index)}
                                                        sx={{marginLeft: "10px", marginTop: "10px", backgroundColor: "#b4b4b6", "&:hover": {backgroundColor: "#9c9ca0"}}}>
                                                <Remove sx={{color: "white"}}/>
                                            </IconButton>
                                        </Grid>
                                    </React.Fragment>
                                ))}
                            </Grid>

                            <Button variant="text" sx={{margin: "10px 0px", padding: "0px", minWidth: "0px", backgroundColor: "#b4b4b6", "&:hover": {backgroundColor: "#9c9ca0"}}}
                                    onClick={handleAddDraftInvoice}>
                                <Add fontSize="large" sx={{color: "white"}}/>
                            </Button>

                            <TextField variant={"outlined"} fullWidth label="Egyéb költségek (Ft)" name={"otherPlannedExpenses"}
                                       value={projectDetails?.otherPlannedExpenses || ""} onChange={onProjectDetailsChange} sx={{marginBottom: "10px"}}/>
                            <TextField variant={"outlined"} multiline fullWidth label="Számla tervek megjegyzés" name={"draftInvoicesDescription"}
                                       value={projectDetails?.draftInvoicesDescription || ""} minRows={3} maxRows={10} onChange={onProjectDetailsChange}/>

                            <div>
                                <Button variant="contained" sx={{marginTop: "10px"}} onClick={updateDraftInvoices}>Mentés</Button>
                            </div>
                        </Collapse>
                    </Box>

                    <Box component="fieldset" sx={{border: (accordionStates?.openInvoices ? "1px solid #e5e7eb" : "none"), borderRadius: "12px", padding: (accordionStates?.openInvoices ? "1rem" : "0px calc(1rem + 1px)"), marginBottom: "20px"}}>
                        <legend style={{fontWeight: "200", padding: "0 .5rem", color: "#575757", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "space-between"}}
                                onClick={() => flipState("openInvoices")}>Számlák {accordionStates?.openInvoices ? <ExpandLess/> : <ExpandMore/>}
                        </legend>
                        <Collapse in={accordionStates?.openInvoices}>
                            <Grid container spacing={1} columns={20}>
                                {invoices.map((invoice, index) => (
                                    <React.Fragment key={index}>
                                        <Grid size={{xs: 4}} sx={{display: "flex"}}>
                                            <FormControl fullWidth>
                                                <InputLabel id="invoice-type-label">Számla típusa</InputLabel>
                                                <Select
                                                    variant="outlined"
                                                    labelId="invoice-type-label"
                                                    name="invoiceType"
                                                    value={invoice?.invoiceType || ""}
                                                    label="Számla típusa"
                                                    sx={{textAlign: "left"}}
                                                >
                                                    <MenuItem value={"INVOICE"}>Számla</MenuItem>
                                                    <MenuItem value={"ADVANCE_INVOICE"}>Előlegszámla</MenuItem>
                                                    <MenuItem value={"FEE_REQUEST"}>Díjbekérő</MenuItem>
                                                    <MenuItem value={"RECEIPT"}>Nyugta</MenuItem>
                                                </Select>
                                            </FormControl>
                                        </Grid>
                                        <Grid size={{xs: 4}}>
                                            <TextField name="invoiceName" value={invoice?.name || ""} fullWidth label="Számla neve"/>
                                        </Grid>
                                        <Grid size={{xs: 4}}>
                                            <TextField name="invoiceAmount" value={invoice?.amount || ""} fullWidth label="Összeg (HUF)"/>
                                        </Grid>
                                        <Grid size={{xs: 4}}>
                                            <TextField
                                                name="invoiceDate"
                                                fullWidth
                                                label="Teljesítés Dátuma"
                                                variant="outlined"
                                                value={invoice?.dateOfInvoice || ""}
                                                type="date"
                                                slotProps={{inputLabel: {shrink: true}}}
                                            />
                                        </Grid>
                                        <Grid size={{xs: 3}}>
                                            <Tooltip title="Szerkesztés" placement="right">
                                                <Button variant="text" component="span" onClick={() => navigate("/dashboard/simple-invoices/ " + invoice.id)}
                                                        sx={{margin: "10px 0px", padding: "0px", minWidth: "0px", backgroundColor: "#b4b4b6", "&:hover": {backgroundColor: "#9c9ca0"},}}>
                                                    <Launch fontSize="large" sx={{color: "white", margin: 0, padding: "5px",}}/>
                                                </Button>
                                            </Tooltip>
                                        </Grid>
                                    </React.Fragment>
                                ))}
                            </Grid>

                            <div>
                                <Button variant="contained" color={"success"} sx={{marginTop: "10px"}} onClick={() => navigate("/dashboard/simple-invoices/create")}>Új Számla</Button>
                            </div>

                        </Collapse>
                    </Box>

                    <Box component="fieldset" sx={{border: "1px solid #e5e7eb", borderRadius: "12px", padding: "1rem"}}>
                        <legend style={{fontWeight: "200", padding: "0 .5rem", color: "#575757"}}>Összefoglaló</legend>
                        <b>Projekt</b>
                        <ul>
                            <li>Név: {project?.name}</li>
                            <li>Projektszám: {project?.identifier}</li>
                            <li>Összérték: {parseToHUF(draftInvoices.map(pt => pt?.amount || 0).reduce((a, b) => a + b, 0) + parseInt(projectDetails?.otherPlannedExpenses))}</li>
                            <li>Készültség: ? %</li>
                            <li>Státusz: {projectDetails.currentState?.name}</li>
                        </ul>

                        <b>Megrendelés</b>
                        <ul></ul>
                        <b>Árajánlat</b>
                        <ul></ul>
                        <b>Termék projektek</b>
                        <ul>
                            {selectedProductProjects?.map((selected, index) => (
                                <li key={index}>{index + 1}. {selected?.name} - Összesen: {parseToHUF(selected?.total)}</li>
                            ))}
                        </ul>

                        <b>Fejlécek</b>
                        <ul>
                            {selectedProjectTasks?.map((selected, index) => (
                                <li key={index}>{index + 1}. {selected?.description}</li>
                            ))}
                        </ul>

                        <b>Tervezett számlák</b>
                        <ul>
                            {draftInvoices?.map((selected, index) => (
                                <li key={index}>{index + 1}. {INVOICE_TYPES[selected?.invoiceType]} | Nettó: {selected?.amount} HUF | Megjegyzés: {selected?.comment}</li>
                            ))}
                        </ul>

                        <b>Valós számlák</b>
                        <ul>
                            {invoices?.map((selected, index) => (
                                <li key={index}>{index + 1}. {INVOICE_TYPES[selected?.invoiceType]} | Nettó: {parseToHUF(selected?.amount / (1 + selected.vat / 100))} | Bruttó: {parseToHUF(selected?.amount)}</li>
                            ))}
                        </ul>

                    </Box>
                </>}

            <Dialog open={openStateSettings} onClose={onSettingsClose} maxWidth="false" sx={{width: "50%", margin: "auto"}}>
                <form onSubmit={updateProjectStates}>
                    <DialogTitle sx={{
                        display: 'flex',
                        alignItems: 'top',
                        justifyContent: 'space-between',
                        bgcolor: '#1976d2;',
                        color: '#fff'
                    }}>
                        <Typography variant="h6">Projekt státusz lehetőségek</Typography>
                    </DialogTitle>
                    <DialogContent sx={{bgcolor: '#ededed'}}>
                        <DialogContentText sx={{mb: 1, paddingTop: "15px", color: "black"}}>
                            <div style={{overflowY: 'scroll', maxHeight: '400px'}}>

                                <Grid container columns={20} spacing={1}>
                                    {projectStates?.map((state, index) => (
                                        <React.Fragment key={index}>
                                            <Grid size={{xs: 18}}>
                                                <TextField name="name" required onChange={e => onProjectStateChange(e, index)} fullWidth value={state?.name}
                                                           sx={{'& input ': {padding: "4px 5px !important"}}}
                                                           autoComplete="off"></TextField>
                                            </Grid>
                                            <Grid size={{xs: 2}}>
                                                <Tooltip title={"Eltávolítás"} placement={"left"}>
                                                    <IconButton sx={{marginTop: "3px", marginRight: "8px", padding: "0px", backgroundColor: "gray"}} onClick={() => removeProjectStateAt(index)}>
                                                        <Remove sx={{color: "white"}}/>
                                                    </IconButton>
                                                </Tooltip>
                                                {index === projectStates?.length - 1 &&
                                                    <Tooltip title={"Hozzáadás"} placement={"right"}>
                                                        <IconButton sx={{marginTop: "3px", padding: "0px", backgroundColor: "gray"}} onClick={() => addProjectState()}>
                                                            <Add sx={{color: "white"}}/>
                                                        </IconButton>
                                                    </Tooltip>
                                                }
                                            </Grid>
                                        </React.Fragment>
                                    ))}
                                </Grid>

                            </div>
                        </DialogContentText>
                    </DialogContent>
                    <DialogActions sx={{p: 2, bgcolor: '#ededed', justifyContent: 'space-between'}}>
                        <Button type="submit" variant="contained">Mentés</Button>
                        <div>
                            <Button onClick={retrieveProjectStates} disabled={!resetStatesButtonEnabled} variant="outlined" sx={{marginRight: "8px"}}>Visszaállítás</Button>
                            <Button onClick={onSettingsClose} variant="outlined">Bezárás</Button>
                        </div>
                    </DialogActions>
                </form>
            </Dialog>

        </Paper>
    );
};

export default ProjectStructure;
